import { Spacer, VDBText } from "@components/atoms";
import { SpaceType } from "@components/atoms/Spacer/types";
import LabelView from "@components/molecules/LabelView";
import Routes from "@navigations/Routes";
import { useNavigationState } from "@react-navigation/native";
import { navigate } from "@navigations/utils/RootNavigation";
import { useSelector } from "react-redux";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import {
  getProductTypeFromRequest,
  renderRequestStatus,
} from "@scenes/requests/utils/RequestsScreen";
import { isMeleeItem } from "@utils/product/productValidators";
import currency from "currency.js";
import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styled, { useTheme } from "styled-components/native";
import { getProductSpecificColor } from "../utils/utils";
import { ItemListProps } from "./ItemListProps";
import { getRequestType, RequestTypes } from "@scenes/requests/utils/utils";
import { useQueryClient } from "@tanstack/react-query";
import { getActiveRouteName } from "@utils/analytics";
import PopUp from "@components/molecules/PopUp";
import { messageType } from "@components/molecules/PopUp/types";
import { ButtonType } from "@components/atoms/Button/types";
import { View } from "react-native";
import { css } from "styled-components/native";
import { getBoxShadow } from "@utils/product/productValidators";
import { PdpSections } from "@utils/customization";
import { DefaultTheme } from "styled-components/native";
import { MemoStatus } from "@src/types/HistoryRequest";
import { VDBIcons } from "@utils/VDBIcons";
import {
  useAllowQuantityOnBulk,
  useConsolidatedStoneTypes,
} from "@redux/slices/globalSlice";
import { ProductCategory } from "@utils/constants";

const isOrderOrQuoteRequest = (requestType?: string) => {
  const type = String(requestType ?? "").toLowerCase();
  return (
    type === RequestTypes.Order.toLowerCase() ||
    type === RequestTypes.Quote.toLowerCase()
  );
};

const ItemDataView = ({
  isReceivedRequest,
  item,
  selectedItem,
  onItemPress,
  selectedTab,
  requestedItem,
  selectedItemId,
}: ItemListProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const typography = useTypographyStore();
  const isCustomApp = useSelector((state: any) => state.appState?.isCustomApp);
  const userInfo = useUserInfo();
  const queryClient = useQueryClient();
  const priceMode = userInfo?.user?.price_mode;
  const isOrangeMode = priceMode === 2;
  const isRead = item?.read;
  const [modifyRequestPopUp, setModifyRequestPopUp] = useState(false);
  const newRouteName = useNavigationState((state) => getActiveRouteName(state));
  const allowQuantityOnBulk = useAllowQuantityOnBulk();
  const consolidatedStoneType = useConsolidatedStoneTypes();

  const markAsRead = () => {
    queryClient.setQueriesData(
      { queryKey: ["stock_item_request"] },
      (data: any) => {
        let foundItem = data?.pages
          ?.map((page: any) => page.data?.data?.stock_item_requests)
          .flat()
          ?.find((e: any) => e.id === item.id);

        if (foundItem && !foundItem?.read) {
          foundItem.read = true;
        }

        return data;
      }
    );
  };

  const onClick = () => {
    if (!isRead) {
      queryClient
        .invalidateQueries({ queryKey: ["GET_UNREAD_REQUEST"] })
        .then();
    }
    markAsRead();
    onItemPress?.(item);
    const productType = getProductTypeFromRequest(
      item?.stock_item_type ?? item?.item?.type ?? item?.request_type ?? ""
    );
    const isQuoteOrder =
      String(item?.order_type ?? "").toLowerCase() === "quote";
    navigate(Routes.RequestDetailScreen, {
      initial: false,
      id: item?.id,
      requestName: isQuoteOrder ? RequestTypes.Order : item?.request_type,
      isReceivedRequest: isReceivedRequest,
      productType: productType,
      price_mode: priceMode,
    });
    if (newRouteName === "request-modify") {
      setModifyRequestPopUp(false);
    }
  };

  const navigationNextScreen = useCallback(() => {
    if (newRouteName !== "request-modify") {
      onClick();
    } else {
      setModifyRequestPopUp(true);
    }
  }, [newRouteName]);

  const getBorderColor = (itemData: any) => {
    return theme.isMobile
      ? "core-03"
      : itemData?.id === (selectedItem?.id || requestedItem?.id) ||
        itemData?.id === Number(selectedItemId)
      ? getProductSpecificColor(itemData?.stock_item_type ?? "")
      : "core-03";
  };

  const totalPriceOfRequest = useMemo(() => {
    const orderedItemsReady = item?.ordered_items_ready;
    if (
      isOrderOrQuoteRequest(item?.request_type) &&
      orderedItemsReady === false
    ) {
      return "-";
    }

    const basePrice = isOrderOrQuoteRequest(item?.request_type)
      ? item?.total_amount
      : item?.offer_total_sales_price &&
        Number(item.offer_total_sales_price) !== 0
      ? item.offer_total_sales_price
      : item?.total_sales_price && Number(item.total_sales_price) !== 0
      ? item?.total_sales_price
      : item?.base_price || item?.item?.base_price;

    const isJewelryBulk =
      allowQuantityOnBulk &&
      getProductTypeFromRequest(
        item?.stock_item_type ?? item?.item?.type ?? item?.request_type ?? ""
      ) === ProductCategory.JEWELRY;

    const finalPrice = isJewelryBulk ? item?.total_amount : basePrice;

    return currency(finalPrice, {
      symbol: item?.currency_symbol ?? item?.item?.currency_symbol ?? "$",
      precision:
        ((Boolean(item?.is_jewelry_order ?? false) === true ||
          item?.stock_item_type === "Jewelry") &&
          item.secondary_request_type !== PdpSections.JEWELRY_BUILDER) ||
        (userInfo?.roundUpPrices() && !isMeleeItem(item?.item || item))
          ? 0
          : 2,
    })?.format();
  }, [allowQuantityOnBulk, item, userInfo]);

  return (
    <View style={{ flex: 1, flexDirection: "row" }}>
      <Card
        key={item?.id}
        onPress={() => navigationNextScreen()}
        bordercolor={getBorderColor(item)}
        read={item?.read}
        selected={
          item?.id === (selectedItem?.id || requestedItem?.id) ||
          item?.id === Number(selectedItemId)
        }
      >
        <HeaderTextContainer>
          <TitleTopContainer>
            {item?.request_type?.includes("bulk") ||
            (item?.request_type?.includes("order") &&
              item?.children_count > 1) ? (
              <VDBText type={typography["Body-R"]} color={theme["core-05"]}>
                {item?.children_count}{" "}
                {item?.children_count === 1
                  ? t("requestHistory.item.text")
                  : t("requestHistory.items.text")}
              </VDBText>
            ) : (
              <VDBText type={typography["Body-R"]} color={theme["core-05"]}>
                {item.short_title ? item.short_title : item?.item?.short_title}
              </VDBText>
            )}
          </TitleTopContainer>

          <LabelContainer
            backgroundColor={
              !item.read &&
              isReceivedRequest &&
              item?.status === "pending" &&
              item?.request_type !== RequestTypes.Order
                ? "core-03"
                : isOrderOrQuoteRequest(item?.request_type) &&
                  item?.track_status
                ? item?.track_status_color
                : item?.memo_status
                ? renderRequestStatus(
                    item?.memo_status
                      ? item?.request_notification_msg?.toLowerCase()
                      : item?.status ?? "",
                    t
                  ).colorCode
                : item?.border_theme
            }
            isHexCode={
              isOrderOrQuoteRequest(item?.request_type) &&
              item?.track_status_color
            }
          >
            <VDBText
              type={[typography["Minor-M"], { textTransform: "capitalize" }]}
              color={
                !item.read &&
                isReceivedRequest &&
                item?.status === "pending" &&
                item?.request_type !== RequestTypes.Order
                  ? theme["link-static"]
                  : theme["core-01"]
              }
            >
              {!item.read &&
              isReceivedRequest &&
              item?.status === "pending" &&
              item?.request_type !== RequestTypes.Order
                ? t("requestHistory.label.new.text")
                : isOrderOrQuoteRequest(item?.request_type) &&
                  item?.track_status
                ? item?.track_status
                : renderRequestStatus(
                    item?.memo_status
                      ? item?.request_notification_msg?.toLowerCase()
                      : item?.status ?? "",
                    t
                  ).status}
            </VDBText>
          </LabelContainer>
        </HeaderTextContainer>
        <MainContainer>
          <RowLabelTextContainer>
            <StockKey>
              <VDBText
                type={typography["Minor-R"]}
                selectable={false}
                numberOfLines={1}
                ellipsizeMode="tail"
              >
                {t("requestHistory.stock.text")}
              </VDBText>
            </StockKey>
            <Gap4 />
            <StockValue>
              <VDBText
                type={typography["Minor-M"]}
                color={theme["core-05"]}
                numberOfLines={1}
                ellipsizeMode="tail"
              >
                {item?.request_type?.includes("bulk") &&
                item?.children_count > 1
                  ? t("requestHistory.multiple.text")
                  : (!isCustomApp &&
                    item?.bmh_request_type !== "retail_buy_request"
                      ? " VDB-"
                      : "") + item.stock_num}
              </VDBText>
            </StockValue>

            <Gap8 />

            <RqstKey>
              <VDBText
                type={typography["Minor-R"]}
                selectable={false}
                numberOfLines={1}
                ellipsizeMode="tail"
              >
                {item?.request_type &&
                getRequestType(item?.request_type) === RequestTypes.Quote
                  ? t("requestHistoryDetail.quoteId.text")
                  : item?.request_type === RequestTypes.Order
                  ? t("requestHistory.orderId.text")
                  : t("requestHistory.requestid.text")}
              </VDBText>
            </RqstKey>
            <Gap4 />
            <RqstValue>
              <VDBText
                type={typography["Minor-M"]}
                color={theme["core-05"]}
                numberOfLines={1}
                ellipsizeMode="tail"
              >
                {item?.request_type !== RequestTypes.Order
                  ? item?.id
                  : item?.order_id}
              </VDBText>
            </RqstValue>
          </RowLabelTextContainer>

          {(!isOrangeMode || isCustomApp) && (
            <>
              <Spacer size={SpaceType.Basic} />
              <LabelLimitTextContainer>
                <Container>
                  <VDBText numberOfLines={1} type={typography["Minor-R"]}>
                    {isReceivedRequest
                      ? t("requestHistory.from.text")
                      : t("requestHistory.to.text")}
                  </VDBText>
                  <Spacer size={SpaceType.ExtraSmall} />
                  <ExtraView>
                    <VDBText
                      color={theme["core-05"]}
                      type={typography["Minor-M"]}
                      numberOfLines={1}
                      ellipsizeMode="tail"
                    >
                      {isOrderOrQuoteRequest(item?.request_type)
                        ? isReceivedRequest
                          ? item?.buyer?.company_name?.replace(/ /g, "\u00A0")
                          : item?.seller?.location_name?.replace(/ /g, "\u00A0")
                        : item?.name?.replace(/ /g, "\u00A0")}
                    </VDBText>
                  </ExtraView>
                </Container>
              </LabelLimitTextContainer>
            </>
          )}
          <Spacer size={SpaceType.Basic} />
          <HeaderTextContainer>
            <LabelTextContainer>
              <LabelView
                rightPrefixText={item.updated_at_date}
                itemRightText={item.updated_at_time}
                valueTypographyStyle={typography["Minor-M"]}
                keyTypographyStyle={typography["Minor-M"]}
                rightPrefixTextTheme={theme["core-05"]}
                hasExtraSpace={true}
              />
            </LabelTextContainer>
            <MainContainer>
              <VDBText
                type={typography["Minor-M"]}
                color={theme[item.theme as keyof DefaultTheme] as string}
              >
                {t(item.type_text)}
                {consolidatedStoneType?.enabled
                  ? ""
                  : item?.product_sub_type
                  ? ` ${item?.product_sub_type}`
                  : ""}
              </VDBText>
            </MainContainer>
          </HeaderTextContainer>
        </MainContainer>
        <Spacer size={SpaceType.Basic} />
        <LineView />

        <PaddingContainer>
          <HeaderTextContainer>
            <LabelTextContainer>
              <LabelView
                leftIcon={
                  userInfo.isPAUserOrPASalesPerson() &&
                  userInfo.isOrangePriceMode() &&
                  item.request_name === RequestTypes.Memo
                    ? RequestTypes.Memo.toLowerCase()
                    : item?.memo_status === MemoStatus.Sold
                    ? VDBIcons.BUY
                    : item?.memo_status === MemoStatus.Return
                    ? VDBIcons.REFRESH_CIRCLE
                    : item.request_icon
                }
                leftIconTheme={theme["core-06"]}
                rightPrefixText={
                  selectedTab === "internal_request"
                    ? RequestTypes.Internal
                    : item.bmh_request_type === "purchasing_agent" &&
                      item.request_name === RequestTypes.Memo
                    ? RequestTypes.Internal
                    : item?.memo_status === MemoStatus.Sold
                    ? t("requestHistory.receivedTab.sold.text", "Sold")
                    : item?.memo_status === MemoStatus.Return
                    ? t("requestHistory.receivedTab.return.text", "Return")
                    : item.request_name
                }
                valueTypographyStyle={typography["Minor-M"]}
                keyTypographyStyle={typography["Minor-M"]}
                containerJustifyContent={"center"}
                itemRightText={
                  item.request_name === "Hold"
                    ? "| " + item.hold_hours
                    : item.hold_hours
                }
              />
            </LabelTextContainer>
            <LabelTextContainer>
              <LabelView
                itemLeftText={t("requestHistory.total.text")}
                keyTypographyStyle={typography["Minor-R"]}
                valueTypographyStyle={typography["Minor-M"]}
                itemRightText={totalPriceOfRequest}
              />
            </LabelTextContainer>
          </HeaderTextContainer>
        </PaddingContainer>
      </Card>
      {modifyRequestPopUp && (
        <PopUp
          type={messageType.Warning}
          title={t("requestHistory.modify.popUp.title.text")}
          buttons={[
            {
              type: ButtonType.primary,
              title: t("requestHistory.modify.popUp.button.submit.text"),
              onPressAction: () => onClick(),
            },
            {
              type: ButtonType.secondary,
              title: t("requestHistory.modify.popUp.button.cancel.text"),
              onPressAction: () => setModifyRequestPopUp(false),
            },
          ]}
          HtmlSource={{
            html: t("requestHistory.modify.popUp.description.text"),
          }}
          styleKey="Body-R"
        />
      )}
    </View>
  );
};

const MainContainer = styled.View`
  padding-left: 5px;
  padding-right: 3px;
`;

const ExtraView = styled.View`
  flex: 1;
  flex-direction: row;
`;

const Container = styled.View`
  flex-direction: row;
  align-items: center;
  padding-right: ${SpaceType.Basic}px;
`;

const LineView = styled.View`
  height: 1px;
  flex-direction: row;
  background-color: ${({ theme }) => theme["core-03"]};
  width: 100%;
  max-width: 100%;
`;

const PaddingContainer = styled.View`
  padding-top: ${SpaceType.Basic}px;
  padding-bottom: ${SpaceType.ExtraSmall}px;
`;

const Card = styled.Pressable<any>`
  flex-direction: column;
  background-color: ${({ theme, read, selected }) =>
    selected ? theme["core-02"] : !read ? theme["core-01"] : theme["core-01"]};
  border-radius: 8px;
  ${({ theme }) =>
    theme.isLargeScreen &&
    css`
      margin-left: 24px;
      margin-right: 20px;
    `}
  margin-horizontal: ${({ theme }) =>
    theme.isMobile
      ? SpaceType.Basic
      : theme.isTablet
      ? SpaceType.Double
      : SpaceType.Big}px;
  margin-bottom: ${SpaceType.Basic}px;
  border-width: ${({ selected }) => (selected ? "2px" : "1px")};
  border-color: ${({ theme, bordercolor }) => theme[bordercolor]};
  flex: 1;
  box-shadow: ${({ theme }) => getBoxShadow(theme.Shadows.Gentle)};
`;

const LabelContainer = styled.View<{
  backgroundColor: string;
  isHexCode: boolean;
}>`
  background-color: ${({ theme, backgroundColor }) => theme[backgroundColor]};
  height: 30px;
  justify-content: center;
  padding: 0px ${SpaceType.Double}px;
  border-top-left-radius: 0px;
  border-top-right-radius: 7px;
  border-bottom-left-radius: 16px;
  border-bottom-right-radius: 0px;
  ${({ isHexCode }) =>
    isHexCode &&
    css`
      background-color: ${({ backgroundColor }) => backgroundColor};
    `}
`;

const TitleTopContainer = styled.View<any>`
  justify-content: space-between;
  align-items: flex-start;
  padding: 4px;
  margin-right: 4px;
  margin-left: ${SpaceType.Basic}px;
  margin-bottom: 4px;
  margin-top: 4px;
  flex-wrap: wrap;
  flex: 1;
`;

const HeaderTextContainer = styled.View`
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
  height: auto;
`;

const LabelTextContainer = styled.View`
  margin-left: ${SpaceType.Basic}px;
  margin-right: ${SpaceType.Basic}px;
  margin-bottom: 4px;
  justify-content: center;
`;

const LabelLimitTextContainer = styled.View`
  margin-left: ${SpaceType.Basic}px;
  margin-right: 12px;
  margin-bottom: 4px;
  justify-content: center;
  flex: 1;
`;

const RowLabelTextContainer = styled.View`
  flex-direction: row;
  margin-left: ${SpaceType.Basic}px;
  margin-right: ${SpaceType.Basic}px;
  margin-bottom: 4px;
`;

const StockKey = styled.View`
  max-width: 15%;
  justify-content: center;
`;

const StockValue = styled.View`
  max-width: 51%;
  justify-content: center;
  overflow: hidden;
`;

const RqstKey = styled.View`
  max-width: 17%;
  justify-content: center;
`;

const RqstValue = styled.View`
  max-width: 22%;
  justify-content: center;
  overflow: hidden;
`;

const Gap4 = styled.View`
  width: 4px;
`;

const Gap8 = styled.View`
  width: 8px;
`;

export default ItemDataView;
